/**
 * CoachingService - Core coaching relationship and student management
 * Feature: 009-coaching-portal
 *
 * Manages:
 * - Coach profiles
 * - Coach-player relationships
 * - Student lists and details
 */

import { BaseService, ServiceResponse } from '@/services/api/base.service';
import type {
  CoachProfile,
  CoachingRelationship,
  CreateCoachProfileInput,
  UpdateCoachProfileInput,
  CreateRelationshipInput,
  UpdateRelationshipInput,
  StudentSummary,
  StudentDetail,
  StudentFilters,
  UserInfo,
} from '@/types/coaching.types';

class CoachingServiceClass extends BaseService {

  // ---------------------------------------------------------------------------
  // Coach Profile Management
  // ---------------------------------------------------------------------------

  /**
   * Get current user's coach profile
   * Prefers the independent profile (club_id IS NULL), falls back to first club profile
   */
  async getMyProfile(): Promise<ServiceResponse<CoachProfile | null>> {
    return this.withErrorHandling(async () => {
      const { data: userData, error: authError } = await this.supabase.auth.getUser();
      if (authError || !userData.user) {
        throw new Error('User not authenticated');
      }

      // Fetch all profiles for this user, ordered by created_at
      const { data: profiles, error } = await this.supabase
        .from('coach_profiles')
        .select('*')
        .eq('user_id', userData.user.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      if (!profiles || profiles.length === 0) return null;

      // Prefer independent profile (club_id IS NULL), fall back to first profile
      const data = profiles.find(p => p.club_id === null) || profiles[0];

      // Then get the user profile separately to avoid join issues
      const { data: userProfile } = await this.supabase
        .from('profiles')
        .select('id, display_name, avatar_url, email')
        .eq('id', userData.user.id)
        .single();

      return {
        ...data,
        user: userProfile ? {
          id: userProfile.id,
          full_name: userProfile.display_name || 'Unknown',
          avatar_url: userProfile.avatar_url,
          email: userProfile.email,
        } as UserInfo : undefined,
      } as CoachProfile;
    }, { operation: 'getMyProfile', userId: (await this.supabase.auth.getUser()).data.user?.id });
  }

  /**
   * Create coach profile for current user
   */
  async createProfile(input: CreateCoachProfileInput): Promise<ServiceResponse<CoachProfile>> {
    return this.withErrorHandling(async () => {
      const { data: userData, error: authError } = await this.supabase.auth.getUser();
      if (authError || !userData.user) {
        throw new Error('User not authenticated');
      }

      const insertData = {
        user_id: userData.user.id,
        club_id: input.club_id ?? null,
        bio: input.bio || null,
        specialties: input.specialties || [],
        certifications: input.certifications || [],
        hourly_rate: input.hourly_rate || null,
        years_experience: input.years_experience || null,
        max_students: input.max_students || 20,
        accepts_new_students: input.accepts_new_students ?? true,
      };

      const { data, error } = await this.supabase
        .from('coach_profiles')
        .insert(insertData)
        .select(`
          *,
          user:profiles!user_id(id, display_name, avatar_url, email)
        `)
        .single();

      if (error) throw error;

      const coachProfile = {
        ...data,
        user: {
          id: data.user.id,
          full_name: data.user.display_name || 'Unknown',
          avatar_url: data.user.avatar_url,
          email: data.user.email,
        } as UserInfo,
      } as CoachProfile;

      // Auto-create affiliations for any pending club invitations for this email
      const userEmail = data.user.email;
      if (userEmail) {
        await this.createPendingClubAffiliations(coachProfile.id, userEmail).catch(err => {
          console.warn('[COACH] Failed to create pending affiliations:', err);
        });
      }

      return coachProfile;
    }, { operation: 'createProfile', userId: (await this.supabase.auth.getUser()).data.user?.id });
  }

  /**
   * When a coach creates their profile, check club_profiles.admin_emails for any
   * pending coach invitations and auto-create the coach_club_affiliations rows.
   */
  private async createPendingClubAffiliations(coachId: string, email: string): Promise<void> {
    // Find all clubs that have this email as a pending coach invite
    const { data: clubProfiles, error } = await this.supabase
      .from('club_profiles')
      .select('id, club_name, admin_emails')
      .not('admin_emails', 'is', null);

    if (error || !clubProfiles) return;

    const normalizedEmail = email.toLowerCase().trim();

    for (const clubProfile of clubProfiles) {
      const adminEmails = clubProfile.admin_emails as any[];
      if (!Array.isArray(adminEmails)) continue;

      const coachInvite = adminEmails.find(
        (entry: any) =>
          typeof entry === 'object' &&
          entry.email?.toLowerCase() === normalizedEmail &&
          entry.role === 'coach' &&
          entry.status === 'pending'
      );

      if (!coachInvite) continue;

      // Get the actual club_id for this club_profile
      const { data: club, error: clubError } = await this.supabase
        .from('clubs')
        .select('id')
        .eq('name', clubProfile.club_name)
        .single();

      if (clubError || !club) continue;

      // Create affiliation (ignore conflict if it already exists)
      const { error: affError } = await this.supabase
        .from('coach_club_affiliations')
        .insert({
          coach_id: coachId,
          club_id: club.id,
          status: 'pending',
          invited_by_club: true,
        })
        .select('id')
        .single();

      if (affError && affError.code !== '23505') {
        console.warn(`[COACH] Failed to create affiliation for club ${club.id}:`, affError);
      } else {
        console.log(`✅ [COACH] Created pending affiliation for club ${club.id}`);
      }
    }
  }

  /**
   * Update current user's coach profile
   */
  async updateProfile(input: UpdateCoachProfileInput): Promise<ServiceResponse<CoachProfile>> {
    return this.withErrorHandling(async () => {
      const { data: userData, error: authError } = await this.supabase.auth.getUser();
      if (authError || !userData.user) {
        throw new Error('User not authenticated');
      }

      const updateData: any = {
        updated_at: new Date().toISOString(),
      };

      // Only include fields that are explicitly provided
      if (input.bio !== undefined) updateData.bio = input.bio;
      if (input.specialties !== undefined) updateData.specialties = input.specialties;
      if (input.certifications !== undefined) updateData.certifications = input.certifications;
      if (input.hourly_rate !== undefined) updateData.hourly_rate = input.hourly_rate;
      if (input.years_experience !== undefined) updateData.years_experience = input.years_experience;
      if (input.max_students !== undefined) updateData.max_students = input.max_students;
      if (input.accepts_new_students !== undefined) updateData.accepts_new_students = input.accepts_new_students;

      const { data, error } = await this.supabase
        .from('coach_profiles')
        .update(updateData)
        .eq('user_id', userData.user.id)
        .select(`
          *,
          user:profiles!user_id(id, display_name, avatar_url, email)
        `)
        .single();

      if (error) throw error;

      // Transform the joined user data
      return {
        ...data,
        user: {
          id: data.user.id,
          full_name: data.user.display_name || 'Unknown',
          avatar_url: data.user.avatar_url,
          email: data.user.email,
        } as UserInfo,
      } as CoachProfile;
    }, { operation: 'updateProfile', userId: (await this.supabase.auth.getUser()).data.user?.id });
  }

  // ---------------------------------------------------------------------------
  // Student Management
  // ---------------------------------------------------------------------------

  /**
   * Get coach's students with optional filters
   */
  async getStudents(filters?: StudentFilters): Promise<ServiceResponse<StudentSummary[]>> {
    return this.withErrorHandling(async () => {
      const { data: userData, error: authError } = await this.supabase.auth.getUser();
      if (authError || !userData.user) {
        throw new Error('User not authenticated');
      }

      // Build the base query
      let query = this.supabase
        .from('coaching_relationships')
        .select(`
          id,
          player_id,
          club_id,
          status,
          focus_areas,
          player:profiles!fk_coaching_relationships_player_profile(id, display_name, avatar_url, handicap)
        `)
        .eq('coach_id', userData.user.id);

      // Apply filters
      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters?.focus_area) {
        query = query.contains('focus_areas', [filters.focus_area]);
      }

      if (filters?.club_id) {
        query = query.eq('club_id', filters.club_id);
      }

      const { data: relationships, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      // Get player IDs to fetch additional data
      const playerIds = relationships?.map((r: any) => r.player_id) || [];

      if (playerIds.length === 0) {
        return [];
      }

      // Fetch coach_profile_id + all supplementary data in parallel
      const clubIds = [...new Set(relationships?.map((r: any) => r.club_id).filter(Boolean) || [])];

      let membershipsQuery = this.supabase
        .from('user_club_memberships')
        .select('user_id, handicap')
        .in('user_id', playerIds);
      if (clubIds.length > 0) {
        membershipsQuery = membershipsQuery.in('club_id', clubIds);
      }

      const [
        { data: coachProfile },
        { data: memberships },
        { data: onboardingData },
      ] = await Promise.all([
        this.supabase
          .from('coach_profiles')
          .select('id')
          .eq('user_id', userData.user.id)
          .limit(1)
          .maybeSingle(),
        membershipsQuery,
        this.supabase
          .from('user_quest_progress')
          .select('user_id, quest_id, completed_at, quest_achievements!inner(league, name, sort_order)')
          .in('user_id', playerIds)
          .in('quest_achievements.league', ['onboarding', 'hcp_54']),
      ]);

      const coachProfileId = coachProfile?.id;

      // Fetch sessions and exercises in parallel (need coachProfileId first)
      const [{ data: lastSessions }, { data: pendingExercises }] = await Promise.all([
        coachProfileId
          ? this.supabase
              .from('coaching_sessions')
              .select('player_id, scheduled_at')
              .eq('coach_id', coachProfileId)
              .in('player_id', playerIds)
              .eq('status', 'completed')
              .order('scheduled_at', { ascending: false })
          : Promise.resolve({ data: null }),
        coachProfileId
          ? this.supabase
              .from('exercise_assignments')
              .select('player_id, id')
              .eq('coach_id', coachProfileId)
              .in('player_id', playerIds)
              .in('status', ['pending', 'in_progress'])
          : Promise.resolve({ data: null }),
      ]);

      // Build lookup maps
      const handicapMap = new Map<string, number>();
      memberships?.forEach(m => {
        if (m.handicap != null) {
          handicapMap.set(m.user_id, m.handicap);
        }
      });
      const lastSessionMap = new Map<string, string>();
      lastSessions?.forEach((s: any) => {
        if (!lastSessionMap.has(s.player_id)) {
          lastSessionMap.set(s.player_id, s.scheduled_at);
        }
      });
      const pendingCountMap = new Map<string, number>();
      pendingExercises?.forEach((e: any) => {
        pendingCountMap.set(e.player_id, (pendingCountMap.get(e.player_id) || 0) + 1);
      });

      // Derive onboarding state from user_quest_progress
      // A player is "in onboarding" if they have any quest_progress rows for onboarding/hcp_54 leagues
      // and have not yet completed all onboarding quests.
      const questProgressMap = new Map<string, any[]>();
      (onboardingData as any[] ?? []).forEach((row: any) => {
        const uid = row.user_id;
        if (!questProgressMap.has(uid)) questProgressMap.set(uid, []);
        questProgressMap.get(uid)!.push(row);
      });

      // Transform to StudentSummary
      const students: StudentSummary[] = relationships
        .map((rel: any) => {
          const playerRows = questProgressMap.get(rel.player_id) ?? [];
          const onboardingRows = playerRows.filter((r: any) => r.quest_achievements?.league === 'onboarding');
          const completedOnboarding = onboardingRows.filter((r: any) => r.completed_at).length;
          const isOnboarding = onboardingRows.length > 0;

          // Current quest = first incomplete onboarding quest (lowest sort_order)
          const incompleteRows = onboardingRows
            .filter((r: any) => !r.completed_at)
            .sort((a: any, b: any) => (a.quest_achievements?.sort_order ?? 0) - (b.quest_achievements?.sort_order ?? 0));
          const currentQuestName = incompleteRows[0]?.quest_achievements?.name ?? undefined;

          return {
            id: rel.player_id,
            relationship_id: rel.id,
            user: {
              id: rel.player.id,
              full_name: rel.player.display_name || 'Unknown',
              avatar_url: rel.player.avatar_url,
            } as UserInfo,
            handicap: handicapMap.get(rel.player_id) ?? rel.player?.handicap ?? null,
            last_session_at: lastSessionMap.get(rel.player_id) || null,
            pending_exercises: pendingCountMap.get(rel.player_id) || 0,
            focus_areas: rel.focus_areas || [],
            status: rel.status,
            // Onboarding fields derived from quest progress
            is_onboarding: isOnboarding,
            onboarding_stage: currentQuestName,
            onboarding_stage_number: completedOnboarding + 1,
            onboarding_group: undefined,
            onboarding_progress_percentage: isOnboarding && onboardingRows.length > 0
              ? Math.round((completedOnboarding / onboardingRows.length) * 100)
              : undefined,
          };
        })
        .filter((student: StudentSummary) => {
          // Apply search filter
          if (filters?.search) {
            const searchLower = filters.search.toLowerCase();
            return student.user.full_name.toLowerCase().includes(searchLower);
          }
          return true;
        });

      return students;
    }, { operation: 'getStudents', userId: (await this.supabase.auth.getUser()).data.user?.id });
  }

  /**
   * Get detailed student view
   */
  async getStudentDetail(playerId: string): Promise<ServiceResponse<StudentDetail>> {
    return this.withErrorHandling(async () => {
      const { data: userData, error: authError } = await this.supabase.auth.getUser();
      if (authError || !userData.user) {
        throw new Error('User not authenticated');
      }

      // Fetch relationship
      // Note: Use explicit FK names to avoid ambiguity (both coach_id and player_id reference profiles)
      const { data: relationship, error: relError } = await this.supabase
        .from('coaching_relationships')
        .select(`
          *,
          coach:profiles!fk_coaching_relationships_coach_profile(id, display_name, avatar_url),
          player:profiles!fk_coaching_relationships_player_profile(id, display_name, avatar_url, email, handicap)
        `)
        .eq('coach_id', userData.user.id)
        .eq('player_id', playerId)
        .single();

      if (relError) throw relError;

      // Look up coach_profile_id (sessions/exercises/assessments/videos FK to coach_profiles)
      const { data: coachProfile } = await this.supabase
        .from('coach_profiles')
        .select('id')
        .eq('user_id', userData.user.id)
        .limit(1)
        .maybeSingle();
      const coachProfileId = coachProfile?.id;

      // Fetch player contact fields directly from profiles
      const { data: playerProfileRow } = await this.supabase
        .from('profiles')
        .select('phone, date_of_birth, home_club')
        .eq('id', playerId)
        .maybeSingle();

      // Fetch player membership — try club-specific first, fall back to any membership
      const { data: membershipWithClub } = await this.supabase
        .from('user_club_memberships')
        .select('id, handicap, membership_type, preferred_tee, phone')
        .eq('user_id', playerId)
        .eq('club_id', relationship.club_id)
        .maybeSingle();

      let membership = membershipWithClub;
      if (!membership) {
        const { data: anyMembership } = await this.supabase
          .from('user_club_memberships')
          .select('id, handicap, membership_type, preferred_tee, phone')
          .eq('user_id', playerId)
          .limit(1)
          .maybeSingle();
        membership = anyMembership;
      }

      // Fetch handicap history from golf_rounds
      const { data: rounds } = await this.supabase
        .from('golf_rounds')
        .select('completed_at, handicap_at_time')
        .eq('user_id', playerId)
        .not('handicap_at_time', 'is', null)
        .order('completed_at', { ascending: false })
        .limit(10);

      const handicapTrend = rounds?.map(r => ({
        date: r.completed_at,
        handicap: r.handicap_at_time,
      })) || [];

      // Fetch recent sessions
      // coaching_sessions.coach_id references coach_profiles(id)
      const { data: sessions } = coachProfileId ? await this.supabase
        .from('coaching_sessions')
        .select(`
          *,
          coach_profile:coach_profiles!coaching_sessions_coach_id_fkey(
            id, user_id
          ),
          player:profiles!coaching_sessions_player_id_fkey(id, display_name, avatar_url)
        `)
        .eq('coach_id', coachProfileId)
        .eq('player_id', playerId)
        .order('scheduled_at', { ascending: false })
        .limit(50) : { data: null };

      // Fetch pending exercises
      // exercise_assignments.coach_id references coach_profiles(id)
      const { data: exercises } = coachProfileId ? await this.supabase
        .from('exercise_assignments')
        .select(`
          *,
          exercise:exercises(*),
          video:video_content(*)
        `)
        .eq('coach_id', coachProfileId)
        .eq('player_id', playerId)
        .in('status', ['pending', 'in_progress', 'completed'])
        .order('assigned_at', { ascending: false }) : { data: null };

      // Fetch all assessments
      // skill_assessments.coach_id references coach_profiles(id)
      const { data: allAssessments } = coachProfileId ? await this.supabase
        .from('skill_assessments')
        .select('*')
        .eq('coach_id', coachProfileId)
        .eq('player_id', playerId)
        .order('assessed_at', { ascending: false }) : { data: null };

      const assessment = allAssessments?.[0] || null;

      // Fetch video assignments for this student
      // video_assignments.coach_id references coach_profiles(id)
      const { data: videoAssignments } = coachProfileId ? await this.supabase
        .from('video_assignments')
        .select(`
          *,
          video:video_id (
            id,
            title,
            description,
            url,
            provider,
            thumbnail_url,
            duration_seconds,
            category,
            tags
          )
        `)
        .eq('coach_id', coachProfileId)
        .eq('player_id', playerId)
        .order('assigned_at', { ascending: false }) : { data: null };

      // Fetch coaching notes
      const { data: coachingNotes } = coachProfileId ? await this.supabase
        .from('coaching_notes')
        .select(`
          *,
          linked_session:coaching_sessions!coaching_notes_linked_session_id_fkey(
            id,
            session_type,
            scheduled_at,
            focus_areas
          )
        `)
        .eq('coach_id', coachProfileId)
        .eq('player_id', playerId)
        .order('created_at', { ascending: false }) : { data: null };

      // Derive onboarding state from user_quest_progress
      const { data: questProgressRows } = await this.supabase
        .from('user_quest_progress')
        .select('user_id, quest_id, completed_at, quest_achievements!inner(league, name, sort_order)')
        .eq('user_id', playerId)
        .in('quest_achievements.league', ['onboarding', 'hcp_54']);

      const onboardingRows = (questProgressRows ?? []).filter(
        (r: any) => r.quest_achievements?.league === 'onboarding'
      );
      const completedOnboarding = onboardingRows.filter((r: any) => r.completed_at).length;
      const isOnboarding = onboardingRows.length > 0;
      const incompleteRows = onboardingRows
        .filter((r: any) => !r.completed_at)
        .sort((a: any, b: any) => (a.quest_achievements?.sort_order ?? 0) - (b.quest_achievements?.sort_order ?? 0));
      const currentQuestName = incompleteRows[0]?.quest_achievements?.name ?? undefined;

      const onboarding = isOnboarding ? {
        is_onboarding: true,
        onboarding_stage: currentQuestName,
        onboarding_stage_number: completedOnboarding + 1,
        onboarding_group: undefined,
        onboarding_progress_percentage: onboardingRows.length > 0
          ? Math.round((completedOnboarding / onboardingRows.length) * 100)
          : 0,
      } : {
        is_onboarding: false,
      };

      // Transform data
      const studentDetail: StudentDetail = {
        id: playerId,
        ...onboarding,
        relationship: {
          ...relationship,
          coach: relationship.coach ? {
            id: relationship.coach.id,
            full_name: relationship.coach.display_name || 'Unknown',
            avatar_url: relationship.coach.avatar_url,
          } as UserInfo : undefined,
          player: relationship.player ? {
            id: relationship.player.id,
            full_name: relationship.player.display_name || 'Unknown',
            avatar_url: relationship.player.avatar_url,
            email: relationship.player.email,
          } as UserInfo : undefined,
        } as CoachingRelationship,
        user: {
          id: relationship.player.id,
          full_name: relationship.player.display_name || 'Unknown',
          avatar_url: relationship.player.avatar_url,
          email: relationship.player.email,
        } as UserInfo,
        player_profile: {
          // Phone: prefer membership phone (club-specific), fall back to profile phone
          phone: (membership as any)?.phone ?? (playerProfileRow as any)?.phone ?? null,
          date_of_birth: (playerProfileRow as any)?.date_of_birth ?? null,
          membership_type: (membership as any)?.membership_type ?? null,
          preferred_tee: (membership as any)?.preferred_tee ?? null,
          membership_id: (membership as any)?.id ?? null,
        },
        handicap: membership?.handicap ?? relationship.player?.handicap ?? null,
        handicap_trend: handicapTrend,
        recent_sessions: sessions || [],
        pending_exercises: exercises || [],
        latest_assessment: assessment || null,
        assessments: allAssessments || [],
        video_assignments: videoAssignments || [],
        notes: (coachingNotes || []) as import('@/types/coaching.types').CoachNote[],
      };

      return studentDetail;
    }, { operation: 'getStudentDetail', userId: (await this.supabase.auth.getUser()).data.user?.id });
  }

  // ---------------------------------------------------------------------------
  // Player Profile (contact info editable by coach)
  // ---------------------------------------------------------------------------

  /**
   * Update a player's contact/profile fields (phone, DOB) and membership fields
   * (membership_type, preferred_tee). Called from the coach's Profile tab edit modal.
   */
  async updatePlayerProfile(
    playerId: string,
    membershipId: string | null,
    data: {
      phone?: string | null;
      date_of_birth?: string | null;
      membership_type?: string | null;
      preferred_tee?: string | null;
    }
  ): Promise<ServiceResponse<boolean>> {
    return this.withErrorHandling(async () => {
      const { phone, date_of_birth, membership_type, preferred_tee } = data;

      // Update profiles table (date_of_birth; phone also saved here as fallback)
      const profileUpdate: Record<string, unknown> = {};
      if (phone !== undefined) profileUpdate.phone = phone;
      if (date_of_birth !== undefined) profileUpdate.date_of_birth = date_of_birth;

      if (Object.keys(profileUpdate).length > 0) {
        const { error } = await this.supabase
          .from('profiles')
          .update(profileUpdate)
          .eq('id', playerId);
        if (error) throw error;
      }

      // Update user_club_memberships (membership_type, preferred_tee, phone)
      if (membershipId) {
        const membershipUpdate: Record<string, unknown> = {};
        if (phone !== undefined) membershipUpdate.phone = phone; // also write to membership
        if (membership_type !== undefined) membershipUpdate.membership_type = membership_type;
        if (preferred_tee !== undefined) {
          membershipUpdate.preferred_tee = preferred_tee;
        }

        if (Object.keys(membershipUpdate).length > 0) {
          const { error } = await this.supabase
            .from('user_club_memberships')
            .update(membershipUpdate)
            .eq('id', membershipId);
          if (error) throw error;
        }
      }

      return true;
    }, { operation: 'updatePlayerProfile', userId: playerId });
  }

  // ---------------------------------------------------------------------------
  // Relationship Management
  // ---------------------------------------------------------------------------

  /**
   * Create a new coach-player relationship
   */
  async createRelationship(input: CreateRelationshipInput): Promise<ServiceResponse<CoachingRelationship>> {
    return this.withErrorHandling(async () => {
      const { data: userData, error: authError } = await this.supabase.auth.getUser();
      if (authError || !userData.user) {
        throw new Error('User not authenticated');
      }

      // Get the coach profile ID first
      // Prefer independent profile (club_id IS NULL) if no club specified
      let profileQuery = this.supabase
        .from('coach_profiles')
        .select('id')
        .eq('user_id', userData.user.id);

      if (input.club_id) {
        profileQuery = profileQuery.eq('club_id', input.club_id);
      } else {
        profileQuery = profileQuery.is('club_id', null);
      }

      const { data: coachProfile, error: profileError } = await profileQuery.single();

      if (profileError || !coachProfile) {
        throw new Error('Coach profile not found. Please create a coach profile first.');
      }

      const insertData = {
        coach_profile_id: coachProfile.id,
        coach_id: userData.user.id,
        player_id: input.player_id,
        club_id: input.club_id ?? null,
        status: 'active' as const,
        focus_areas: input.focus_areas || [],
        goals: input.goals || null,
        started_at: new Date().toISOString(),
      };

      const { data, error } = await this.supabase
        .from('coaching_relationships')
        .insert(insertData)
        .select(`
          *,
          coach:profiles!fk_coaching_relationships_coach_profile(id, display_name, avatar_url),
          player:profiles!fk_coaching_relationships_player_profile(id, display_name, avatar_url)
        `)
        .single();

      if (error) throw error;

      // Transform the joined data
      return {
        ...data,
        coach: data.coach ? {
          id: data.coach.id,
          full_name: data.coach.display_name || 'Unknown',
          avatar_url: data.coach.avatar_url,
        } as UserInfo : undefined,
        player: data.player ? {
          id: data.player.id,
          full_name: data.player.display_name || 'Unknown',
          avatar_url: data.player.avatar_url,
        } as UserInfo : undefined,
      } as CoachingRelationship;
    }, { operation: 'createRelationship', userId: (await this.supabase.auth.getUser()).data.user?.id });
  }

  /**
   * Update an existing relationship
   */
  async updateRelationship(
    relationshipId: string,
    input: UpdateRelationshipInput
  ): Promise<ServiceResponse<CoachingRelationship>> {
    return this.withErrorHandling(async () => {
      const { data: userData, error: authError } = await this.supabase.auth.getUser();
      if (authError || !userData.user) {
        throw new Error('User not authenticated');
      }

      const updateData: any = {
        updated_at: new Date().toISOString(),
      };

      // Only include fields that are explicitly provided
      if (input.status !== undefined) {
        updateData.status = input.status;
        if (input.status === 'ended') {
          updateData.ended_at = new Date().toISOString();
        }
      }
      if (input.focus_areas !== undefined) updateData.focus_areas = input.focus_areas;
      if (input.goals !== undefined) updateData.goals = input.goals;
      if (input.notes !== undefined) updateData.notes = input.notes;

      const { data, error } = await this.supabase
        .from('coaching_relationships')
        .update(updateData)
        .eq('id', relationshipId)
        .eq('coach_id', userData.user.id) // Ensure coach owns this relationship
        .select(`
          *,
          coach:profiles!fk_coaching_relationships_coach_profile(id, display_name, avatar_url),
          player:profiles!fk_coaching_relationships_player_profile(id, display_name, avatar_url)
        `)
        .single();

      if (error) throw error;

      // Transform the joined data
      return {
        ...data,
        coach: data.coach ? {
          id: data.coach.id,
          full_name: data.coach.display_name || 'Unknown',
          avatar_url: data.coach.avatar_url,
        } as UserInfo : undefined,
        player: data.player ? {
          id: data.player.id,
          full_name: data.player.display_name || 'Unknown',
          avatar_url: data.player.avatar_url,
        } as UserInfo : undefined,
      } as CoachingRelationship;
    }, { operation: 'updateRelationship', userId: (await this.supabase.auth.getUser()).data.user?.id });
  }

  // ---------------------------------------------------------------------------
  // Coach Discovery (for players)
  // ---------------------------------------------------------------------------

  /**
   * Get available coaches for a club
   * Returns coaches who accept new students
   */
  async getAvailableCoaches(clubId: string): Promise<ServiceResponse<CoachProfile[]>> {
    return this.withErrorHandling(async () => {
      // IMPORTANT:
      // PostgREST can only do implicit joins when a FK exists.
      // Our schema has coach_profiles.user_id -> auth.users(id), NOT -> profiles(id),
      // so we cannot join coach_profiles -> profiles directly in a select.
      const { data, error } = await this.supabase
        .from('coach_profiles')
        .select('*')
        .eq('club_id', clubId)
        .eq('accepts_new_students', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const coachProfiles = (data || []) as CoachProfile[];
      const userIds = coachProfiles.map((cp) => cp.user_id).filter(Boolean);

      // Fetch user profiles separately and stitch them into the result
      const { data: userProfiles, error: profilesError } = await this.supabase
        .from('profiles')
        .select('id, display_name, avatar_url, email')
        .in('id', userIds);

      if (profilesError) throw profilesError;

      const profileMap = new Map(
        (userProfiles || []).map((p: any) => [
          p.id,
          {
            id: p.id,
            full_name: p.display_name || 'Unknown',
            avatar_url: p.avatar_url,
            email: p.email,
          } as UserInfo,
        ])
      );

      return coachProfiles.map((cp) => ({
        ...cp,
        user: profileMap.get(cp.user_id),
      })) as CoachProfile[];
    }, { operation: 'getAvailableCoaches' });
  }

  /**
   * Request coaching from a coach (player-initiated)
   * Creates a coaching relationship
   */
  async requestCoach(coachProfileId: string, clubId: string): Promise<ServiceResponse<CoachingRelationship>> {
    return this.withErrorHandling(async () => {
      const { data: userData, error: authError } = await this.supabase.auth.getUser();
      if (authError || !userData.user) {
        throw new Error('User not authenticated');
      }

      // Get the coach profile to get the coach's user_id
      const { data: coachProfile, error: profileError } = await this.supabase
        .from('coach_profiles')
        .select('id, user_id')
        .eq('id', coachProfileId)
        .eq('club_id', clubId)
        .single();

      if (profileError || !coachProfile) {
        throw new Error('Coach profile not found');
      }

      // Check if relationship already exists
      const { data: existing } = await this.supabase
        .from('coaching_relationships')
        .select('*')
        .eq('coach_profile_id', coachProfileId)
        .eq('player_id', userData.user.id)
        .maybeSingle();

      if (existing) {
        if (existing.status === 'active') {
          throw new Error('You already have an active coaching relationship with this coach');
        } else if (existing.status === 'paused') {
          throw new Error('You have a paused relationship with this coach. Please contact them to resume.');
        }
      }

      // Create the relationship
      const insertData = {
        coach_profile_id: coachProfileId,
        coach_id: coachProfile.user_id,
        player_id: userData.user.id,
        club_id: clubId,
        status: 'active' as const,
        focus_areas: [],
        goals: null,
        started_at: new Date().toISOString(),
      };

      const { data, error } = await this.supabase
        .from('coaching_relationships')
        .insert(insertData)
        .select('*')
        .single();

      if (error) throw error;

      // Fetch coach and player profiles separately
      const [coachProfileData, playerProfileData] = await Promise.all([
        this.supabase
          .from('profiles')
          .select('id, display_name, avatar_url')
          .eq('id', coachProfile.user_id)
          .single(),
        this.supabase
          .from('profiles')
          .select('id, display_name, avatar_url, email')
          .eq('id', userData.user.id)
          .single(),
      ]);

      return {
        ...data,
        coach: coachProfileData.data ? {
          id: coachProfileData.data.id,
          full_name: coachProfileData.data.display_name || 'Unknown',
          avatar_url: coachProfileData.data.avatar_url,
        } as UserInfo : undefined,
        player: playerProfileData.data ? {
          id: playerProfileData.data.id,
          full_name: playerProfileData.data.display_name || 'Unknown',
          avatar_url: playerProfileData.data.avatar_url,
          email: playerProfileData.data.email,
        } as UserInfo : undefined,
      } as CoachingRelationship;
    }, { operation: 'requestCoach', userId: (await this.supabase.auth.getUser()).data.user?.id });
  }
}

// Export singleton instance
export const coachingService = new CoachingServiceClass();
export { CoachingServiceClass as CoachingService };
